import { useCallback, useEffect, useState } from 'react'
import { api, type InstanceStatus } from '../api'

interface Props {
  onSwitchWorld: () => void
}

function ServiceBadge({ status }: { status: string }) {
  if (status === 'active')   return <span className="badge badge-green">active</span>
  if (status === 'inactive') return <span className="badge badge-red">inactive</span>
  return <span className="badge badge-gray">{status}</span>
}

function OnlinePlayers({ players }: { players: string[] }) {
  if (players.length === 0) {
    return <span className="text-muted" style={{ fontSize: '0.8rem' }}>No players online</span>
  }
  return (
    <div className="flex flex-wrap gap-1" style={{ marginTop: 4 }}>
      {players.map(p => (
        <span
          key={p}
          className="badge badge-green"
          style={{ fontSize: '0.75rem', padding: '2px 8px' }}
        >
          {p}
        </span>
      ))}
    </div>
  )
}

function InstanceCard({
  title,
  inst,
  onSwitch,
}: {
  title:    string
  inst:     InstanceStatus
  onSwitch?: () => void
}) {
  return (
    <div className="card">
      <div className="card-title">{title}</div>
      <div className="flex items-center gap-3 mb-4">
        <ServiceBadge status={inst.service} />
        {inst.permanent && <span className="badge badge-gray">permanent</span>}
      </div>
      <div className="stat-row">
        <div className="stat-item">
          <span className="stat-label">World</span>
          <span className="stat-value">{inst.world || '—'}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Online now</span>
          <OnlinePlayers players={inst.onlinePlayers ?? []} />
        </div>
        <div className="stat-item">
          <span className="stat-label">Recent joins (1 hr)</span>
          <span className="stat-value">{inst.recentJoins}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Connect</span>
          <span className="connect-addr">{inst.connectAddress}</span>
        </div>
      </div>

      {onSwitch && (
        <div className="mt-4">
          <button
            className="btn btn-primary btn-sm w-full"
            onClick={onSwitch}
            disabled={inst.service === 'inactive'}
          >
            Switch world
          </button>
        </div>
      )}
    </div>
  )
}

export default function Dashboard({ onSwitchWorld }: Props) {
  const [status,  setStatus]  = useState<{ instance1: InstanceStatus; instance2: InstanceStatus } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)
  const [lastAt,  setLastAt]  = useState<Date | null>(null)

  const refresh = useCallback(async () => {
    try {
      const data = await api.getStatus()
      setStatus(data)
      setError(null)
      setLastAt(new Date())
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
    const id = setInterval(refresh, 30_000)
    return () => clearInterval(id)
  }, [refresh])

  return (
    <>
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Dashboard</h1>
          <p>Server status — refreshes every 30 s</p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={refresh} disabled={loading}>
          {loading ? <span className="spinning">◌</span> : '↻'} Refresh
        </button>
      </div>

      {lastAt && (
        <p className="refresh-note">Last updated {lastAt.toLocaleTimeString()}</p>
      )}

      {error && <p className="error-msg mb-4">⚠ {error}</p>}

      {loading && !status ? (
        <p className="text-muted text-sm">Loading…</p>
      ) : status ? (
        <div className="grid-2">
          <InstanceCard
            title="Instance 1 — Permanent"
            inst={status.instance1}
          />
          <InstanceCard
            title="Instance 2 — Switchable"
            inst={status.instance2}
            onSwitch={onSwitchWorld}
          />
        </div>
      ) : null}
    </>
  )
}
